import React from 'react';
import { generateClient } from 'aws-amplify/api';
import { userDataByUserId, legacyBookByUserId, createUserData, updateUserData } from '../userDataGraphql';
import { getUserData } from '../graphql/queries';
import { safeParseLS } from '../utils/contentHelpers';
import { isAuthExpiry } from '../utils/authExpiry';
import { withStoryRemovedEverywhere, withStoryRemovedFromBook, clearTombstones } from '../utils/bookHelpers';

/**
 * pickLegacyBookForConfirm — pure. Prefer the UserData row writes update (preferredId),
 * not a blind userDataByUserId(limit:1).items[0] which can be a different row.
 */
export function pickLegacyBookForConfirm(getUserDataRecord, listItems, preferredId) {
  if (getUserDataRecord && getUserDataRecord.legacyBook != null) return getUserDataRecord.legacyBook;
  const items = Array.isArray(listItems) ? listItems : [];
  const match = preferredId ? items.find(it => it && it.id === preferredId) : null;
  return (match || items[0])?.legacyBook ?? null;
}

/**
 * mergeBooks — pure function, safe to call in tests.
 *
 * Merges a server-side books array with a local (in-memory) books array using
 * an additive-by-ID strategy that prevents last-write-wins clobber across devices.
 *
 * Rules:
 *  - Union of all book IDs from server + local (no book is ever dropped).
 *  - For books present in BOTH arrays:
 *    - Pages are merged as a union by storyId (no page is ever dropped)...
 *    - ...MINUS the book's `removedStoryIds` TOMBSTONES (F-252). See below.
 *    - Metadata (title, coverStyle, etc.) uses LOCAL values for the active book,
 *      SERVER values for every other book (the other device's edits are preserved).
 *  - For books present only in server: keep server copy.
 *  - For books present only in local: keep local copy.
 *
 * TOMBSTONES (F-252, founder ruling on [V-251] option 1) — why they are required:
 * the page union above is ADDITIVE ONLY, so a page removed on this device is still present in
 * the server copy and gets re-added by the union on the very next write. Removal was therefore
 * impossible to express: the F-250 sweep found 17 pages pointing at deleted stories, created by
 * exactly this path (entry deleted, pointer resurrected, then hidden because every render skips
 * an unresolvable page). A removal is now recorded as an ADD to `removedStoryIds` — the one
 * operation this union propagates correctly — and subtracted after the union.
 *
 * Tombstone resolution follows the SAME rule the metadata already uses, for consistency:
 *  - ACTIVE book: LOCAL `removedStoryIds` is authoritative. This is the book being edited, so an
 *    explicit remove (adds the id) and an explicit re-add (clears it, see `withPageAdded`) both
 *    stick instead of being undone by the server copy.
 *  - EVERY OTHER book: the UNION of server + local tombstones, so a "delete forever" — which
 *    tombstones the story in every book — propagates even though non-active books otherwise take
 *    server metadata.
 * KNOWN TRADE-OFF (unchanged in kind from the pre-F-252 behaviour, and now detectable): a device
 * holding stale state for the ACTIVE book can still undo another device's removal of that book's
 * page, because local is authoritative there. If the story was deleted outright, that resurrects
 * a dangling pointer — which PAGE-LINK-1's dangling-page watchdog now alerts on, where before
 * F-252 the same race was silent and unmeasured.
 *
 * @param {Array}  serverBooks   Books array loaded from DynamoDB.
 * @param {Array}  localBooks    Books array currently in memory on this device.
 * @param {string} activeBookId  The book being edited right now; its local metadata wins.
 * @returns {Array} Merged books array. Idempotent — calling twice with same inputs = same output.
 */
export function mergeBooks(serverBooks, localBooks, activeBookId) {
  const server = Array.isArray(serverBooks) ? serverBooks : [];
  const local = Array.isArray(localBooks) ? localBooks : [];

  // Build lookup maps keyed by book id
  const serverMap = new Map(server.map(b => [b.id, b]));
  const localMap = new Map(local.map(b => [b.id, b]));

  // Collect all unique book IDs maintaining insertion order (server first, then local-only)
  const allIds = [...new Set([...server.map(b => b.id), ...local.map(b => b.id)])];

  return allIds.map(id => {
    const s = serverMap.get(id);
    const l = localMap.get(id);

    if (s && l) {
      // Book exists on both devices — merge pages, resolve metadata conflict
      const serverPages = Array.isArray(s.pages) ? s.pages : [];
      const localPages = Array.isArray(l.pages) ? l.pages : [];

      // Union of pages by storyId, resolved FIELD BY FIELD rather than whole-object.
      //
      // F-301 (supersedes the F-253 audit note that used to sit here). Until now this union kept
      // the SERVER page object wholesale for any storyId present on both sides, so an UPDATE to an
      // existing page was discarded on the very next write — and the discarded version was what
      // persistBooks then wrote back. The F-253 audit corrected the stale comment on these lines
      // and explicitly left the behaviour alone as "outside F-252"; V-300 is the bill for that.
      // A paying customer enhanced 9 photos for a hardcover: runEnhancement swaps only
      // pages[].photoPath, so every enhancement was dropped here. His stored book held 7-of-8
      // pages with photoPath null, and the one page that kept a path kept the value it was FIRST
      // INSERTED with — after insertion nothing could move it. Chapter reordering was lost the
      // same way, because order came from serverPages too.
      //
      // Resolution follows the rule the metadata and tombstones below ALREADY use, so the whole
      // function now resolves conflicts one way instead of two:
      //   ACTIVE book   — local wins. It is the book in front of the user, so their edit sticks.
      //                   Local page ORDER is authoritative; a page only the server has is still
      //                   appended, never dropped.
      //   OTHER books   — server wins, unchanged. saveBookData always names the edited book as
      //                   active, so a page update to a background book is not a path the app
      //                   takes; the one change is that a field the server LACKS is now filled
      //                   from local instead of thrown away.
      //
      // Both directions fail CLOSED toward keeping the customer's content: a key the winning side
      // does not carry at all falls through to the other side rather than blanking. An explicit
      // `null` is NOT an absence — it is the user clearing a photo (BookBuilderPage.js:4057) and
      // it wins on the active book like any other local value.
      //
      // KNOWN TRADE-OFF, same one F-252 already accepted for metadata and tombstones and now
      // stated for page fields: a device holding stale state for the ACTIVE book can overwrite
      // another device's newer field values on that book. That is a two-device race. The
      // behaviour it replaces lost EVERY page update on a SINGLE device, every time.
      const localById = new Map();
      const localUnkeyed = [];
      for (const lp of localPages) {
        if (!lp) continue;
        if (lp.storyId != null) localById.set(lp.storyId, lp); else localUnkeyed.push(lp);
      }
      const serverById = new Map();
      const serverUnkeyed = [];
      for (const sp of serverPages) {
        if (!sp) continue;
        if (sp.storyId != null) serverById.set(sp.storyId, sp); else serverUnkeyed.push(sp);
      }
      const localWins = id === activeBookId;
      // Ordered by whichever side is authoritative, then the other side's extras appended.
      const orderedIds = localWins
        ? [...new Set([...localById.keys(), ...serverById.keys()])]
        : [...new Set([...serverById.keys(), ...localById.keys()])];
      const mergedPages = orderedIds.map(storyId => {
        const sp = serverById.get(storyId);
        const lp = localById.get(storyId);
        if (!sp) return lp;
        if (!lp) return sp;
        return localWins ? { ...sp, ...lp } : { ...lp, ...sp };
      });
      // A page with no storyId cannot be keyed, so it cannot be field-merged — but it is still
      // the customer's row and dropping it here would be exactly the silent loss this change
      // exists to stop. Carried through on the authoritative side. (The old whole-object union
      // kept server-side unkeyed pages implicitly; this keeps that, and stops losing local ones.)
      const carried = localWins ? localUnkeyed : serverUnkeyed;
      if (carried.length) mergedPages.push(...carried);

      // F-252 TOMBSTONES: local is authoritative for the ACTIVE book (so a remove or a re-add
      // performed right here sticks); union everywhere else (so a delete-forever propagates).
      const sTomb = Array.isArray(s.removedStoryIds) ? s.removedStoryIds : [];
      const lTomb = Array.isArray(l.removedStoryIds) ? l.removedStoryIds : [];
      const tombstones = id === activeBookId ? new Set(lTomb) : new Set([...sTomb, ...lTomb]);

      // Metadata: local wins for the book being actively edited, server wins otherwise
      const metaSource = id === activeBookId ? l : s;
      return {
        ...metaSource,
        id,
        pages: mergedPages.filter(p => !(p && tombstones.has(p.storyId))),
        removedStoryIds: [...tombstones],
      };
    }

    if (s) return s; // only on server — keep it
    return l;        // only local — keep it
  });
}

/**
 * Custom hook encapsulating all UserData DynamoDB persistence logic.
 * Manages: recipients, shares, capsules, books, collections, journal,
 * account settings, and all related save/load operations.
 */
export default function useUserData(userId, displayName) {
  const client = React.useMemo(() => generateClient(), []);
  // -- Core UserData state --
  const [userDataId, setUserDataId] = React.useState(null);
  const [userDataLoaded, setUserDataLoaded] = React.useState(false);

  const [recipients, setRecipients] = React.useState([]);
  const [shares, setShares] = React.useState({});
  const [capsules, setCapsules] = React.useState([]);
  const [books, setBooks] = React.useState([]);
  const booksRef = React.useRef([]);
  const [activeBookId, setActiveBookId] = React.useState(null);
  const [collections, setCollections] = React.useState([]);
  const [journalEntries, setJournalEntries] = React.useState([]);
  const [memoryCareSubjects, setMemoryCareSubjects] = React.useState([]);

  // -- Account Settings state --
  const [accountSettings, setAccountSettings] = React.useState({});
  const [accountName, setAccountName] = React.useState(displayName || '');
  const [legacyMode, setLegacyMode] = React.useState('self');
  const [legacySubjectName, setLegacySubjectName] = React.useState('');
  const [notifyCheckin, setNotifyCheckin] = React.useState(true);
  const [notifyRelease, setNotifyRelease] = React.useState(true);
  const [notifyCapsule, setNotifyCapsule] = React.useState(true);
  const [releaseMethod, setReleaseMethod] = React.useState('trusted');
  const [checkinFrequency, setCheckinFrequency] = React.useState('90');

  // -- Estate Planning state (persisted inside accountSettings blob) --
  const [estateChecks, setEstateChecks] = React.useState({});
  const [estateNotes, setEstateNotes] = React.useState({});
  const [estateContacts, setEstateContacts] = React.useState({});

  // Debounce ref for estate saves -- avoids a DynamoDB write on every keystroke/checkbox
  const estateDebounceRef = React.useRef(null);

  // -- Premium status -- DynamoDB is authoritative, localStorage is a fast initial cache --
  // Seeded from localStorage so there is no locked-UI flash before DynamoDB loads.
  // applyAccountSettings (called on every DynamoDB load) overwrites this with the real value.
  // DevTools localStorage manipulation does NOT persist across reloads because DynamoDB
  // overwrites the value on every session load.
  const [isPremium, setIsPremium] = React.useState(() => {
    try { return localStorage.getItem('remnant_isPremium') === 'true'; } catch { return false; }
  });

  // Derived
  const isKeeper = legacyMode === 'other' && legacySubjectName.trim();
  const subjectName = isKeeper ? legacySubjectName : accountName || 'you';

  // -- DynamoDB persistence engine --
  const userDataRef = React.useRef({ id: null, saving: false, pending: null });

  const persistUserData = React.useCallback(async (field, value) => {
    const ref = userDataRef.current;
    const input = { [field]: JSON.stringify(value) };

    if (ref.saving) {
      ref.pending = { ...ref.pending, ...input };
      return;
    }

    ref.saving = true;
    let result;
    try {
      if (ref.id) {
        result = await client.graphql({ query: updateUserData, variables: { input: { id: ref.id, ...input } } });
      } else {
        result = await client.graphql({ query: createUserData, variables: { input: { userId, ...input } } });
        ref.id = result.data.createUserData.id;
        setUserDataId(ref.id);
      }
    } catch (err) {
      console.error('Failed to persist user data:', err);
    } finally {
      ref.saving = false;
      if (ref.pending) {
        const queued = ref.pending;
        ref.pending = null;
        const id = ref.id;
        if (id) {
          ref.saving = true;
          try {
            await client.graphql({ query: updateUserData, variables: { input: { id, ...queued } } });
          } catch (err) { console.error('Failed to flush queued update:', err); }
          finally { ref.saving = false; }
        }
      }
    }
    return result;
  }, [userId]);

  // -- Save wrappers --
  const saveRecipients = (updated) => { setRecipients(updated); persistUserData('recipients', updated); };
  const saveShares = (updated) => { setShares(updated); persistUserData('shares', updated); };
  const saveCapsules = (updated) => { setCapsules(updated); persistUserData('capsules', updated); };
  const saveCollections = (updated) => { setCollections(updated); persistUserData('collections', updated); };
  const saveJournalEntries = (updated) => { setJournalEntries(updated); persistUserData('journal', updated); };
  const saveMemoryCareSubjects = (updated) => { setMemoryCareSubjects(updated); persistUserData('memoryCareSubjects', updated); };
  const saveAccountSettings = (updates) => {
    const updated = { ...accountSettings, ...updates };
    setAccountSettings(updated);
    persistUserData('accountSettings', updated);
  };

  // -- Estate Planning save helpers (debounced -- estate data changes on every keystroke/toggle) --
  // All three fields are stored as sub-keys inside the accountSettings blob so no schema change is needed.
  const saveEstateDataDebounced = React.useCallback((checksVal, notesVal, contactsVal) => {
    if (estateDebounceRef.current) clearTimeout(estateDebounceRef.current);
    estateDebounceRef.current = setTimeout(() => {
      setAccountSettings(prev => {
        const updated = { ...prev, estateChecks: checksVal, estateNotes: notesVal, estateContacts: contactsVal };
        persistUserData('accountSettings', updated);
        return updated;
      });
    }, 800);
  }, [persistUserData]);

  const saveEstateChecks = React.useCallback((updated) => {
    setEstateChecks(updated);
    setAccountSettings(prev => {
      const merged = { ...prev, estateChecks: updated };
      if (estateDebounceRef.current) clearTimeout(estateDebounceRef.current);
      estateDebounceRef.current = setTimeout(() => {
        persistUserData('accountSettings', merged);
      }, 800);
      return merged;
    });
  }, [persistUserData]);

  const saveEstateNotes = React.useCallback((updated) => {
    setEstateNotes(updated);
    setAccountSettings(prev => {
      const merged = { ...prev, estateNotes: updated };
      if (estateDebounceRef.current) clearTimeout(estateDebounceRef.current);
      estateDebounceRef.current = setTimeout(() => {
        persistUserData('accountSettings', merged);
      }, 800);
      return merged;
    });
  }, [persistUserData]);

  const saveEstateContacts = React.useCallback((updated) => {
    setEstateContacts(updated);
    setAccountSettings(prev => {
      const merged = { ...prev, estateContacts: updated };
      if (estateDebounceRef.current) clearTimeout(estateDebounceRef.current);
      estateDebounceRef.current = setTimeout(() => {
        persistUserData('accountSettings', merged);
      }, 800);
      return merged;
    });
  }, [persistUserData]);

  // -- Book persistence --
  // Merge-before-write: re-fetches the server's legacyBook before writing so that
  // concurrent device edits are merged additively rather than last-write-wins.
  // The active book is identified by activeBookIdRef so closures never capture a
  // stale value of activeBookId from React state.
  const activeBookIdRef = React.useRef(activeBookId);
  React.useEffect(() => { activeBookIdRef.current = activeBookId; }, [activeBookId]);

  const persistBooks = React.useCallback(async (updatedBooks, explicitActiveBookId) => {
    // Update local state immediately so the UI never lags
    setBooks(updatedBooks);
    booksRef.current = updatedBooks;

    const resolvedActiveBookId = explicitActiveBookId || activeBookIdRef.current;

    // Re-fetch the server's current legacyBook value and merge before writing.
    // If the fetch fails we fall back to the local-only write (safe degradation).
    let booksToWrite = updatedBooks;
    try {
      const fetchResult = await client.graphql({
        query: legacyBookByUserId,
        variables: { userId, limit: 1 },
      });
      const items = fetchResult?.data?.userDataByUserId?.items;
      if (items && items.length > 0) {
        const rawLegacyBook = items[0].legacyBook;
        if (rawLegacyBook) {
          let parsed;
          try {
            const first = JSON.parse(rawLegacyBook);
            parsed = typeof first === 'string' ? JSON.parse(first) : first;
          } catch {
            parsed = null;
          }
          const serverBooks = parsed?.books && Array.isArray(parsed.books) ? parsed.books : [];
          booksToWrite = mergeBooks(serverBooks, updatedBooks, resolvedActiveBookId);
          // Keep local state in sync with the merged result so the caller sees the
          // full picture (e.g. a book created on another device appears locally).
          // Only update if the merge actually added content beyond what local had.
          // F-253 (noted, accepted): this syncs on ADDITIONS only, so a removal that came from
          // ANOTHER device's tombstone is written to the server correctly but is not reflected in
          // local state until the next load. The stale page disappears on reload; it is never
          // printed either way, because the PDF build skips any page whose story is missing.
          if (booksToWrite.length > updatedBooks.length ||
              booksToWrite.some((mb, i) => mb.pages?.length > (updatedBooks[i]?.pages?.length ?? 0))) {
            setBooks(booksToWrite);
            booksRef.current = booksToWrite;
          }
        }
      }
    } catch (fetchErr) {
      console.warn('[persistBooks] Re-fetch for merge failed — falling back to local-only write:', fetchErr);
      // booksToWrite stays as updatedBooks (local-only write)
    }

    return persistUserData('legacyBook', { books: booksToWrite });
  }, [userId, client, persistUserData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Accepts an explicit bookId fallback for callers that just created a book
  // via ensureBookExists() — React state for activeBookId may not have flushed
  // yet at the time of save, so callers should pass the synchronously-known
  // bookId from ensureBookExists.
  const saveBookData = (bookData, explicitBookId) => {
    const bookId = explicitBookId || activeBookId;
    if (!bookId) {
      console.warn('[saveBookData] skipped — no activeBookId and no explicitBookId provided');
      return Promise.resolve();
    }
    // F-252: this is the single choke point every page-ADD flows through, so tombstone clearing
    // lives here rather than in each add path. Any storyId present in the incoming pages is an
    // intentional (re-)add and must not stay tombstoned, or mergeBooks would strip it right back
    // out. A removal is unaffected: the removed page is absent, so its tombstone survives.
    const updated = booksRef.current.map(b =>
      b.id === bookId ? clearTombstones({ ...b, ...bookData }, bookData && bookData.pages) : b
    );
    // Pass bookId as the explicit active book so mergeBooks knows whose metadata wins
    return persistBooks(updated, bookId);
  };

  // F-252: a confirmed DELETE FOREVER must clear the page from EVERY book, which saveBookData
  // cannot express (it writes one book). Callers must have already confirmed the LegacyEntry
  // delete — this only fixes up the book blob.
  const removeStoryPageEverywhere = React.useCallback((storyId) => {
    if (!storyId) return;
    persistBooks(withStoryRemovedEverywhere(booksRef.current, storyId), activeBookIdRef.current);
  }, [persistBooks]); // eslint-disable-line react-hooks/exhaustive-deps

  // F-252: "Remove from this book" — non-destructive, so only this book is tombstoned and the
  // story stays available to re-add later.
  const removeStoryPageFromBook = React.useCallback((bookId, storyId) => {
    if (!bookId || !storyId) return;
    persistBooks(withStoryRemovedFromBook(booksRef.current, bookId, storyId), bookId);
  }, [persistBooks]); // eslint-disable-line react-hooks/exhaustive-deps

  // F-75: authoritative check that a story's book PAGE actually landed in the server's legacyBook. The page
  // write (saveBookData → persistBooks → persistUserData('legacyBook')) is fire-and-forget and can be dropped
  // by a last-write-wins race on the blob; the reveal Save gates "kept"/removeDraft on this, so a lost page
  // fails HONESTLY (draft retained) instead of silently orphaning the entry + showing a false "kept".
  // Retries with a short settle delay so the async write has time to land before we conclude it didn't.
  const confirmBookPagePersisted = React.useCallback(async (bookId, storyId, attempts = 4) => {
    if (!bookId || !storyId) return false;
    for (let i = 0; i < attempts; i++) {
      await new Promise(r => setTimeout(r, i === 0 ? 700 : 500));
      try {
        // Prefer the same UserData row writes update (ref.id), not a blind limit:1 items[0]
        // which can be a different row when multiple UserData records exist for the user.
        let getRecord = null;
        let listItems = null;
        const preferredId = userDataRef.current?.id;
        if (preferredId) {
          try {
            const byId = await client.graphql({ query: getUserData, variables: { id: preferredId } });
            getRecord = byId?.data?.getUserData || null;
          } catch (e) { /* fall through to list-by-userId */ }
        }
        if (getRecord == null || getRecord.legacyBook == null) {
          const res = await client.graphql({ query: userDataByUserId, variables: { userId, limit: 25 } });
          listItems = res?.data?.userDataByUserId?.items || [];
        }
        const raw = pickLegacyBookForConfirm(getRecord, listItems, preferredId);
        if (raw) {
          let parsed;
          try { const first = JSON.parse(raw); parsed = typeof first === 'string' ? JSON.parse(first) : first; } catch { parsed = null; }
          const books = parsed && Array.isArray(parsed.books) ? parsed.books : [];
          const book = books.find(b => b.id === bookId);
          if (book && Array.isArray(book.pages) && book.pages.some(pg => pg.storyId === storyId)) return true;
        }
      } catch (e) { /* transient - retry */ }
    }
    return false;
  }, [userId, client]);

  // -- Apply account settings from a parsed record --
  const applyAccountSettings = (acct) => {
    setAccountSettings(acct);
    if (acct.displayName) setAccountName(acct.displayName);
    if (acct.legacyMode) setLegacyMode(acct.legacyMode);
    if (acct.legacySubjectName) setLegacySubjectName(acct.legacySubjectName);
    if (acct.notifyCheckin !== undefined) setNotifyCheckin(acct.notifyCheckin);
    if (acct.notifyRelease !== undefined) setNotifyRelease(acct.notifyRelease);
    if (acct.notifyCapsule !== undefined) setNotifyCapsule(acct.notifyCapsule);
    if (acct.releaseMethod) setReleaseMethod(acct.releaseMethod);
    if (acct.checkinFrequency) setCheckinFrequency(acct.checkinFrequency);
    // Estate data stored inside the accountSettings blob
    if (acct.estateChecks) setEstateChecks(acct.estateChecks);
    if (acct.estateNotes) setEstateNotes(acct.estateNotes);
    if (acct.estateContacts) setEstateContacts(acct.estateContacts);
    // Premium status -- DynamoDB value overwrites the localStorage fast-cache on every load.
    // false when the field is absent (free user) or explicitly false.
    const dbPremium = acct.isPremium === true;
    setIsPremium(dbPremium);
    try { localStorage.setItem('remnant_isPremium', dbPremium ? 'true' : 'false'); } catch {}
  };

  // -- Load UserData from DynamoDB (with localStorage migration) --
  // onFirstBookLoaded callback lets App.js set builder UI state from the first book
  const loadUserData = async (onFirstBookLoaded) => {
    // Read the last-open book id synchronously BEFORE any await, so the
    // persist effect in App.js (which runs after this effect on mount) can't
    // clobber it. '' is the sentinel for "user was on the My Books list".
    const savedActiveBookId = (() => { try { return localStorage.getItem('tb_activeBookId'); } catch { return null; } })();
    try {
      const result = await client.graphql({ query: userDataByUserId, variables: { userId, limit: 1 } });
      const items = result.data.userDataByUserId.items;

      if (items.length > 0) {
        const record = items[0];
        userDataRef.current.id = record.id;
        setUserDataId(record.id);

        // parse() handles AppSync AWSJSON fields. AppSync may return the DDB String
        // value single- or double-JSON-encoded depending on how data was written (via
        // AppSync mutation vs. direct DDB boto3 write). If the first JSON.parse returns
        // a string (not the expected object/array), attempt a second parse so fixture
        // data seeded directly into DDB works identically to mutation-written data.
        const parse = (val, fallback) => {
          try {
            if (!val) return fallback;
            const first = JSON.parse(val);
            if (typeof first === 'string') {
              try { return JSON.parse(first); } catch { return first; }
            }
            return first;
          } catch { return fallback; }
        };

        setRecipients(parse(record.recipients, []));
        setShares(parse(record.shares, {}));
        setCapsules(parse(record.capsules, []));

        // Multi-book migration: detect old vs new format
        const rawBook = parse(record.legacyBook, {});
        let loadedBooks = [];
        if (rawBook.books && Array.isArray(rawBook.books)) {
          loadedBooks = rawBook.books;
        } else if (rawBook.title || rawBook.pages) {
          const genId = () => crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36);
          const personalBook = {
            id: genId(), bookType: 'PERSONAL',
            title: rawBook.title || 'My Stories', subtitle: rawBook.subtitle || '', author: rawBook.author || '',
            dedication: rawBook.dedication || '', coverStyle: rawBook.coverStyle || 'classic',
            coverPhotoPath: rawBook.coverPhotoPath || null, pages: rawBook.pages || [],
            createdAt: new Date().toISOString(),
          };
          loadedBooks.push(personalBook);
          if (rawBook.celebrationBooks && Array.isArray(rawBook.celebrationBooks)) {
            rawBook.celebrationBooks.forEach(cb => {
              loadedBooks.push({
                id: cb.id || genId(), bookType: 'GIFT',
                title: cb.bookTitle || cb.title || 'Celebration Book', subtitle: cb.subtitle || '', author: cb.author || '',
                dedication: cb.dedication || '', coverStyle: cb.coverTemplate || cb.coverStyle || 'classic',
                coverPhotoPath: cb.coverPhotoPath || null, pages: cb.pages || [],
                occasion: cb.occasion, recipientName: cb.recipientName,
                deliveryDate: cb.deliveryDate, contributionDeadline: cb.contributionDeadline,
                orderByDate: cb.orderByDate, contributors: cb.contributors || [],
                createdAt: cb.createdAt || new Date().toISOString(),
              });
            });
          }
          persistUserData('legacyBook', { books: loadedBooks });
        }
        setBooks(loadedBooks);
        booksRef.current = loadedBooks;

        // Restore the book the user last had open (survives refresh). Prefer the
        // saved id; fall back to the first book. '' means they were on the list,
        // so preload the first book's content but don't open it.
        if (loadedBooks.length > 0) {
          let target = loadedBooks[0];
          let openBook = true;
          if (savedActiveBookId === '') {
            openBook = false;
          } else if (savedActiveBookId) {
            target = loadedBooks.find(b => b.id === savedActiveBookId) || loadedBooks[0];
          }
          if (openBook) setActiveBookId(target.id);
          if (onFirstBookLoaded) onFirstBookLoaded(target);
        }

        setCollections(parse(record.collections, []));
        setJournalEntries(parse(record.journal, []));
        setMemoryCareSubjects(parse(record.memoryCareSubjects, []));

        const parsedAccountSettings = parse(record.accountSettings, {});

        // One-time localStorage migration for estate data that pre-dates DynamoDB persistence.
        // If the DynamoDB record exists but has no estate data yet, pull from localStorage and
        // immediately write it into the accountSettings blob so it is not lost on the next load.
        let needsMigration = false;
        if (!parsedAccountSettings.estateChecks) {
          const lsChecks = safeParseLS(`remnant_estate_checks_${userId}`, null);
          if (lsChecks && Object.keys(lsChecks).length > 0) {
            parsedAccountSettings.estateChecks = lsChecks;
            needsMigration = true;
          }
        }
        if (!parsedAccountSettings.estateNotes) {
          const lsNotes = safeParseLS(`remnant_estate_notes_${userId}`, null);
          if (lsNotes && Object.keys(lsNotes).length > 0) {
            parsedAccountSettings.estateNotes = lsNotes;
            needsMigration = true;
          }
        }
        // estateContacts had no localStorage key -- nothing to migrate, but guard defensively
        if (!parsedAccountSettings.estateContacts) {
          parsedAccountSettings.estateContacts = {};
        }

        // One-time migration for isPremium from the legacy standalone localStorage key.
        // If DynamoDB has no isPremium field yet but localStorage has it set to true
        // (a paying beta user), promote that value into the accountSettings blob.
        if (parsedAccountSettings.isPremium === undefined) {
          try {
            const lsPremium = localStorage.getItem('remnant_isPremium') === 'true';
            if (lsPremium) {
              parsedAccountSettings.isPremium = true;
              needsMigration = true;
            }
          } catch {}
        }

        applyAccountSettings(parsedAccountSettings);

        if (needsMigration) {
          // Persist all migrated data (estate + isPremium) to DynamoDB in one write
          try {
            await client.graphql({ query: updateUserData, variables: { input: { id: record.id, accountSettings: JSON.stringify(parsedAccountSettings) } } });
          } catch (migErr) {
            console.error('localStorage migration to DynamoDB failed:', migErr);
          }
        }

        // Clean up localStorage after successful DynamoDB load (including migrated estate keys)
        try {
          localStorage.removeItem(`remnant_recipients_${userId}`);
          localStorage.removeItem(`remnant_shares_${userId}`);
          localStorage.removeItem(`remnant_capsules_${userId}`);
          localStorage.removeItem(`remnant_account_${userId}`);
          localStorage.removeItem(`remnant_book_${userId}`);
          localStorage.removeItem(`remnant_collections_${userId}`);
          localStorage.removeItem(`remnant_estate_checks_${userId}`);
          localStorage.removeItem(`remnant_estate_notes_${userId}`);
        } catch {}

      } else {
        // No DynamoDB record -- check localStorage for migration
        const lsRecipients = safeParseLS(`remnant_recipients_${userId}`, []);
        const lsShares = safeParseLS(`remnant_shares_${userId}`, {});
        const lsCapsules = safeParseLS(`remnant_capsules_${userId}`, []);
        const lsAccount = safeParseLS(`remnant_account_${userId}`, {});
        const lsBook = safeParseLS(`remnant_book_${userId}`, { title: 'My Stories', dedication: '', storyIds: [] });
        const lsCollections = safeParseLS(`remnant_collections_${userId}`, []);

        // Carry over isPremium from the legacy standalone localStorage key if not already in lsAccount
        if (lsAccount.isPremium === undefined) {
          try {
            const lsPremium = localStorage.getItem('remnant_isPremium') === 'true';
            if (lsPremium) lsAccount.isPremium = true;
          } catch {}
        }

        setRecipients(lsRecipients);
        setShares(lsShares);
        setCapsules(lsCapsules);

        const genId2 = () => crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36);
        // Only seed a book when the user actually has legacy book data to migrate.
        // A brand-new user has none — leave books empty and activeBookId null so the
        // first-run warm chooser ("write my own story" vs "celebrate someone") shows,
        // instead of auto-creating + entering a phantom "My Stories" book.
        const hasLegacyBook = (Array.isArray(lsBook.pages) && lsBook.pages.length > 0)
          || (Array.isArray(lsBook.storyIds) && lsBook.storyIds.length > 0);
        const migratedBooks = hasLegacyBook
          ? [{ id: genId2(), bookType: 'PERSONAL', title: lsBook.title || 'My Stories', subtitle: '', author: '', dedication: lsBook.dedication || '', coverStyle: 'classic', coverPhotoPath: null, pages: lsBook.pages || [], createdAt: new Date().toISOString() }]
          : [];
        setBooks(migratedBooks);
        booksRef.current = migratedBooks;
        if (migratedBooks.length > 0) { setActiveBookId(migratedBooks[0].id); }
        setCollections(lsCollections);
        applyAccountSettings(lsAccount);

        const hasData = lsRecipients.length > 0 || lsCapsules.length > 0 || Object.keys(lsAccount).length > 0
          || lsBook.storyIds?.length > 0 || lsCollections.length > 0 || Object.keys(lsShares).length > 0;

        if (hasData) {
          try {
            const input = {
              userId,
              recipients: JSON.stringify(lsRecipients),
              shares: JSON.stringify(lsShares),
              capsules: JSON.stringify(lsCapsules),
              accountSettings: JSON.stringify(lsAccount),
              legacyBook: JSON.stringify({ books: migratedBooks }),
              collections: JSON.stringify(lsCollections),
            };
            const createResult = await client.graphql({ query: createUserData, variables: { input } });
            userDataRef.current.id = createResult.data.createUserData.id;
            setUserDataId(createResult.data.createUserData.id);
            console.log('Successfully migrated localStorage data to DynamoDB');

            try {
              localStorage.removeItem(`remnant_recipients_${userId}`);
              localStorage.removeItem(`remnant_shares_${userId}`);
              localStorage.removeItem(`remnant_capsules_${userId}`);
              localStorage.removeItem(`remnant_account_${userId}`);
              localStorage.removeItem(`remnant_book_${userId}`);
              localStorage.removeItem(`remnant_collections_${userId}`);
            } catch {}
          } catch (migErr) {
            console.error('Migration to DynamoDB failed, using localStorage for now:', migErr);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load user data from DynamoDB:', err);
      // TB-096: a dead session must NOT fall through to the empty-account localStorage
      // fallback (which renders as catastrophic data loss). Re-throw so the App-level
      // .catch routes to the reassuring re-auth screen instead.
      if (isAuthExpiry(err)) throw err;
      // Fallback: load from localStorage
      setRecipients(safeParseLS(`remnant_recipients_${userId}`, []));
      setShares(safeParseLS(`remnant_shares_${userId}`, {}));
      setCapsules(safeParseLS(`remnant_capsules_${userId}`, []));
      const lsFallbackBook = safeParseLS(`remnant_book_${userId}`, {});
      if (lsFallbackBook.title || lsFallbackBook.pages) {
        const fb = [{ id: Math.random().toString(36).substring(2), bookType: 'PERSONAL', title: lsFallbackBook.title || 'My Stories', pages: lsFallbackBook.pages || [], dedication: lsFallbackBook.dedication || '', coverStyle: 'classic', subtitle: '', author: '', coverPhotoPath: null, createdAt: new Date().toISOString() }];
        setBooks(fb); booksRef.current = fb; setActiveBookId(fb[0].id);
      }
      setCollections(safeParseLS(`remnant_collections_${userId}`, []));
      const lsFallbackAccount = safeParseLS(`remnant_account_${userId}`, {});
      // Include estate data from localStorage in the DynamoDB-failed fallback path
      if (!lsFallbackAccount.estateChecks) {
        const lsChecks = safeParseLS(`remnant_estate_checks_${userId}`, null);
        if (lsChecks) lsFallbackAccount.estateChecks = lsChecks;
      }
      if (!lsFallbackAccount.estateNotes) {
        const lsNotes = safeParseLS(`remnant_estate_notes_${userId}`, null);
        if (lsNotes) lsFallbackAccount.estateNotes = lsNotes;
      }
      applyAccountSettings(lsFallbackAccount);
    } finally {
      setUserDataLoaded(true);
    }
  };

  return {
    // State
    userDataId, userDataLoaded,
    recipients, setRecipients,
    shares, setShares,
    capsules, setCapsules,
    books, setBooks, booksRef,
    activeBookId, setActiveBookId,
    collections, setCollections,
    journalEntries, setJournalEntries,
    memoryCareSubjects, setMemoryCareSubjects,
    accountSettings, setAccountSettings,
    accountName, setAccountName,
    legacyMode, setLegacyMode,
    legacySubjectName, setLegacySubjectName,
    notifyCheckin, setNotifyCheckin,
    notifyRelease, setNotifyRelease,
    notifyCapsule, setNotifyCapsule,
    releaseMethod, setReleaseMethod,
    checkinFrequency, setCheckinFrequency,
    // Estate Planning (persisted inside accountSettings blob, source of truth is DynamoDB)
    estateChecks, setEstateChecks,
    estateNotes, setEstateNotes,
    estateContacts, setEstateContacts,

    // Premium -- DynamoDB-authoritative (localStorage is a fast initial cache only)
    isPremium, setIsPremium,

    // Derived
    isKeeper, subjectName,

    // Functions
    persistUserData,
    saveRecipients, saveShares, saveCapsules,
    saveCollections, saveAccountSettings, saveJournalEntries, saveMemoryCareSubjects,
    saveEstateChecks, saveEstateNotes, saveEstateContacts,
    persistBooks, saveBookData, confirmBookPagePersisted,
    removeStoryPageEverywhere, removeStoryPageFromBook,
    loadUserData,
  };
}
